// Barre « Fichiers » des écrans Inventaire et Pré-inventaire : export Excel (modèle papier A4), PDF, modèle vierge et import d'un .xlsx.
import { useRef, useState } from 'react';
import { API_BASE, telechargerFichier } from '../api/fichiers.ts';
import { Message } from './ui.tsx';

interface Props {
  periode: { id: string; mois: number; annee: number };
  code: 'inventaire' | 'preinventaire';
  lectureSeule: boolean;
  /** Reçoit le fichier choisi ; renvoie le message de résultat à afficher. */
  onImport: (fichier: File) => Promise<string>;
}

const NOMS = { inventaire: 'Inventaire_FANOME', preinventaire: 'Pre_inventaire' } as const;

export default function BarreFichier({ periode, code, lectureSeule, onImport }: Props) {
  const [enCours, setEnCours] = useState<string | null>(null);
  const [msg, setMsg] = useState<{ niveau: 'info' | 'warn'; texte: string } | null>(null);
  const champ = useRef<HTMLInputElement>(null);
  const suffixe = `${periode.mois}-${periode.annee}`;
  const url = (format: 'xlsx' | 'pdf', vierge = false) => `${API_BASE}/export/${periode.id}/tableau/${code}?format=${format}${vierge ? '&vierge=1' : ''}`;

  async function exporter(cle: string, format: 'xlsx' | 'pdf', vierge = false) {
    setEnCours(cle); setMsg(null);
    try { await telechargerFichier(url(format, vierge), `${NOMS[code]}${vierge ? '_VIERGE' : `_${suffixe}`}.${format}`); }
    catch (e: any) { setMsg({ niveau: 'warn', texte: `${e.message ?? 'Échec du téléchargement.'} (l'export nécessite une connexion)` }); }
    finally { setEnCours(null); }
  }
  async function importer(f: File | undefined) {
    if (!f) return;
    setEnCours('import'); setMsg(null);
    try { setMsg({ niveau: 'info', texte: await onImport(f) }); }
    catch (e: any) { setMsg({ niveau: 'warn', texte: e.message ?? "Import impossible. Vérifiez votre connexion et le fichier (.xlsx)." }); }
    finally { setEnCours(null); if (champ.current) champ.current.value = ''; }
  }

  return (
    <div className="outils barre-actions">
      <button className="secondaire" disabled={enCours !== null} onClick={() => exporter('xlsx', 'xlsx')}>{enCours === 'xlsx' ? 'Génération…' : '📊 Exporter Excel (A4)'}</button>
      <button className="secondaire" disabled={enCours !== null} onClick={() => exporter('pdf', 'pdf')}>{enCours === 'pdf' ? 'Génération…' : '📄 Exporter PDF'}</button>
      <button className="secondaire" disabled={enCours !== null} onClick={() => exporter('vierge', 'xlsx', true)} title="Document papier vierge à imprimer">🖨️ Modèle vierge</button>
      {!lectureSeule && (<>
        <button disabled={enCours !== null} onClick={() => champ.current?.click()} title="Remplit la saisie du mois avec un fichier Excel (.xlsx) du même modèle">{enCours === 'import' ? 'Lecture…' : '📥 Importer un fichier Excel'}</button>
        <input ref={champ} type="file" accept=".xlsx,application/vnd.openxmlformats-officedocument.spreadsheetml.sheet" hidden onChange={(e) => importer(e.target.files?.[0])} />
      </>)}
      {msg && <Message niveau={msg.niveau}>{msg.texte}</Message>}
    </div>
  );
}
